import { ChevronIcon } from '@repo/atomic-components';
import { classNames } from 'primereact/utils';
import { FLOATING_PANEL_SHADOW } from '@/constants';

// Themed Dropdown `pt` block reused across the form sidebars. Returns
// the `pt`/`dropdownIcon`/`collapseIcon` triplet so callers can spread
// it onto a PrimeReact Dropdown:
//
//   <Dropdown
//     {...themedDropdownPt()}
//     options={...}
//     value={...}
//     onChange={...}
//   />
//
// Row colours: focused (mouse-over / arrow-key) → gray-50; selected
// (current value) → green-50. Selected wins so the green tint stays
// steady when the mouse moves over the active row.
export const themedDropdownPt = () => ({
  dropdownIcon: <ChevronIcon className="[&_path]:!stroke-fletchers-gray-200" />,
  collapseIcon: <ChevronIcon className="[&_path]:!stroke-fletchers-gray-200 rotate-180" />,
  pt: {
    root: {
      className: '!h-8 !border-fletchers-gray-110 hover:!border-fletchers-gray-110 !outline-none !outline-offset-0',
    },
    input: { className: '!text-[14px]/[20px] !py-0 !pl-2 flex !items-center' },
    panel: {
      className: `border border-fletchers-gray-110 !rounded-[4px] overflow-hidden translate-y-[4px] ${FLOATING_PANEL_SHADOW}`,
    },
    wrapper: { className: '!mt-0' },
    item: ({ context }: { context: { selected: boolean; focused: boolean } }) => ({
      className: classNames('!pl-2', {
        '!bg-fletchers-green-50 hover:!bg-fletchers-green-50': context.selected,
        '!bg-fletchers-gray-50 hover:!bg-fletchers-gray-50': !context.selected && context.focused,
      }),
    }),
  },
});

// Variant that stretches the panel to the field's full width (default
// PrimeReact panel sizes to its content) and anchors flush left. The
// input truncates with an ellipsis instead of pushing the neighbouring
// button off-screen — used in the download modals where a long template
// name would otherwise blow out the row layout. Pair with
// `appendTo="self"` on the Dropdown so the panel stays inside the modal
// and its clicks don't trigger any outside-click closer.
export const fullWidthThemedDropdownPt = () => {
  const themed = themedDropdownPt();
  return {
    ...themed,
    pt: {
      ...themed.pt,
      root: {
        // min-w-0 lets the flex child shrink so its input can truncate.
        // Don't set overflow-hidden here: `appendTo="self"` makes the panel
        // a descendant of the root, and clipping the root would clip the
        // open panel too.
        className: `${themed.pt.root.className} min-w-0`,
      },
      input: {
        className: `${themed.pt.input.className} !truncate`,
      },
      panel: {
        className: `${themed.pt.panel.className} !w-full !left-0`,
      },
    },
  };
};
